import {
	AlertBanner,
	ButtonSubmit,
	Colour,
	ColourStateSolid,
	ComboBoxItemDisplayEnum,
	ComboBoxTypeEnum,
	CountryCodeControl,
	FormWrapperLabel,
	ValidationPatterns,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { Controller, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect, useState } from 'react';
import { addAddress, AddressDataUpdateType, useUpdateAddressData } from '@ukpostbox/client-data';
import { UniqueEmailControl } from '../../UniqueEmailControl';
import { ValidTelephone } from '../../../../Settings/Components/AddAddress/ValidTelephone';
import { AddAddressType } from '../Types/AddAddressTypes';

function AddAddress(props: AddAddressType) {
	const { t } = useTranslation();

	const formMethods = useForm({
		defaultValues: {
			name: props.existingData?.name ?? '',
			businessName: props.existingData?.businessName ?? '',
			propertyName: props.existingData?.propertyName ?? '',
			addressLine1: props.existingData?.addressLine1 ?? '',
			addressLine2: props.existingData?.addressLine2 ?? '',
			addressLine3: props.existingData?.addressLine3 ?? '',
			postCode: props.existingData?.postCode ?? '',
			countryCode: props.existingData?.countryCode ?? '',
			phoneNo: props.existingData?.phoneNo ?? '',
			emailAddress: props.existingData?.emailAddress ?? '',
			flatNumber: props.existingData?.flatNumber ?? '',
			propertyNumber: props.existingData?.propertyNumber ?? '',
		},
		mode: 'onChange',
	});

	const {
		control,
		register,
		handleSubmit,
		setError,
		getValues,
		clearErrors,
		setValue,
		reset,
		setFocus,
		formState: { errors, isValid },
		watch,
	} = formMethods;

	const [addressError, setAddressError] = useState<any>();

	const [addressSaving, setAddressSaving] = useState<boolean>(false);
	const [telephoneLookupActive, setTelephoneLookupActive] = useState<boolean>(false);
	const [emailLookupActive, setEmailLookupActive] = useState<boolean>(false);

	const [telephoneValid, setTelephoneValid] = useState<boolean>(!!props.existingData?.phoneNo);
	const [emailValid, setEmailValid] = useState<boolean>(!!props.existingData?.emailAddress);

	const updateAddressData = useUpdateAddressData();

	const HandleSave = (data: any, event: any) => {
		setAddressSaving(true);
		addAddress(data)
			.then((r: any) => {
				updateAddressData.mutate({
					newData: {
						value: r.addressId,
						object: { ...r, ...data },
						label: `${data.name}, ${data.propertyNameNumber}, ${data.addressLine1}`,
					},
					updateType: AddressDataUpdateType.CREATE,
				});

				if (props.onAddCallback) {
					props.onAddCallback({ ...data, addressId: r.addressId });
				}

				reset();
			})
			.catch((e) => {
				setAddressError(e.toString());
			})
			.finally(() => setAddressSaving(false));
	};

	useEffect(() => {
		if (props.existingData) {
			setValue('name', props.existingData.name);
			setValue('businessName', props.existingData.businessName);
			setValue('propertyName', props.existingData.propertyName);
			setValue('addressLine1', props.existingData.addressLine1);
			setValue('addressLine2', props.existingData.addressLine2);
			setValue('addressLine3', props.existingData.addressLine3);
			setValue('postCode', props.existingData.postCode);
			setValue('countryCode', props.existingData.countryCode);
			setValue('phoneNo', props.existingData.phoneNo);
			setValue('emailAddress', props.existingData.emailAddress);
			setValue('flatNumber', props.existingData.flatNumber as string);
			setValue('propertyNumber', props.existingData.propertyNumber as string);
		} else {
			setValue('name', '');
			setValue('businessName', '');
			setValue('propertyName', '');
			setValue('addressLine1', '');
			setValue('addressLine2', '');
			setValue('addressLine3', '');
			setValue('postCode', '');
			setValue('countryCode', '');
			setValue('phoneNo', '');
			setValue('emailAddress', '');
			setValue('flatNumber', '');
			setValue('propertyNumber', '');
		}
		setFocus('name');
	}, [props]);

	useEffect(() => {
		const subscription = watch((e) => {
			console.log('watch', e, emailValid, telephoneValid, errors);
		});

		return () => subscription.unsubscribe();
	}, [watch, emailValid, telephoneValid, errors]);

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={handleSubmit((data, e) => HandleSave(data, e))}>
				<fieldset className='flex-column flex-gap-md'>
					<FormWrapperLabel
						label={t('address.labels.name')}
						invalid={errors.name && errors.name?.message !== ''}
						validationMessage={errors.name?.message as string}
						showValidationMarks
					>
						<input
							type='text'
							aria-invalid={errors.name ? 'true' : 'false'}
							{...register('name', {
								required: 'This is a required field',
								minLength: { value: 1, message: 'Too short' },
							})}
						/>
					</FormWrapperLabel>
					<FormWrapperLabel label={t('address.labels.businessName')}>
						<input
							type='text'
							aria-invalid={errors.businessName ? 'true' : 'false'}
							{...register('businessName', {
								minLength: { value: 1, message: 'Must be longer than 1 character' },
							})}
						/>
					</FormWrapperLabel>
					<FormWrapperLabel
						label={t('address.labels.propertyNameNumber')}
						invalid={errors.propertyName && errors.propertyName?.message !== ''}
						validationMessage={errors.propertyName?.message as string}
						showValidationMarks
					>
						<input
							type='text'
							aria-invalid={errors.propertyName ? 'true' : 'false'}
							{...register('propertyName', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>
					<FormWrapperLabel
						label={t('address.labels.street')}
						invalid={errors.addressLine1 && errors.addressLine1?.message !== ''}
						validationMessage={errors.addressLine1?.message as string}
						showValidationMarks
					>
						<input
							type='text'
							aria-invalid={errors.addressLine1 ? 'true' : 'false'}
							{...register('addressLine1', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>
					<FormWrapperLabel
						label={t('address.labels.town')}
						invalid={errors.addressLine2 && errors.addressLine2?.message !== ''}
						validationMessage={errors.addressLine2?.message as string}
						showValidationMarks
					>
						<input
							type='text'
							aria-invalid={errors.addressLine2 ? 'true' : 'false'}
							{...register('addressLine2', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>
					<FormWrapperLabel
						label={t('address.labels.city')}
						invalid={errors.addressLine3 && errors.addressLine3?.message !== ''}
						validationMessage={errors.addressLine3?.message as string}
						showValidationMarks
					>
						<input
							type='text'
							aria-invalid={errors.addressLine3 ? 'true' : 'false'}
							{...register('addressLine3', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>
					<FormWrapperLabel
						label={t('address.labels.postCode')}
						invalid={errors.postCode && errors.postCode?.message !== ''}
						validationMessage={errors.postCode?.message as string}
						showValidationMarks
					>
						<input
							type='text'
							aria-invalid={errors.postCode ? 'true' : 'false'}
							{...register('postCode', {
								required: 'This is a required field',
								minLength: { value: 2, message: 'Must be longer than 2 characters' },
							})}
						/>
					</FormWrapperLabel>
					<Controller
						control={control}
						name='countryCode'
						defaultValue={getValues().countryCode}
						rules={{ required: 'Country Code is required' }}
						render={({ field, fieldState }) => (
							<CountryCodeControl
								placeholder={t('address.labels.countryCode')}
								name='Country Code'
								displayType={ComboBoxTypeEnum.Single}
								displayItemLabelType={ComboBoxItemDisplayEnum.Label}
								forwardValueCallback={(val) => field.onChange(val)}
								defaultValue={[getValues().countryCode]}
							/>
						)}
					/>
					<Controller
						control={control}
						name='phoneNo'
						defaultValue={getValues().phoneNo}
						rules={{ required: 'Telephone is required' }}
						render={({ field, fieldState }) => (
							<ValidTelephone
								fieldState={fieldState}
								placeholder='Telephone Number'
								name={field.name}
								defaultValue={field.value}
								changeCallback={(e) => {
									setTelephoneValid(false);
									field.onChange(e);
								}}
								validationCallback={(message, valid) => {
									setTelephoneValid(valid);
									if (valid) {
										clearErrors(field.name);
									} else {
										setError(field.name, { type: 'custom', message });
									}
								}}
								lookupActiveCallback={(state) => setTelephoneLookupActive(state)}
							/>
						)}
					/>
					<Controller
						control={control}
						name='emailAddress'
						defaultValue={getValues().emailAddress}
						rules={{
							required: 'Email Address is required',
							pattern: { value: ValidationPatterns.email, message: 'E-mail format is incorrect' },
						}}
						render={({ field, fieldState }) => (
							<UniqueEmailControl
								name={field.name}
								defaultValue={field.value}
								fieldState={fieldState}
								label='Email Address'
								placeholder='Email Address'
								changeCallback={(e) => {
									setEmailValid(false);
									field.onChange(e);
								}}
								validationCallback={(message, valid) => {
									setEmailValid(isValid);
									if (valid) {
										clearErrors(field.name);
									} else {
										setError(field.name, { type: 'custom', message });
									}
								}}
								lookupActiveCallback={(state) => setEmailLookupActive(state)}
							/>
						)}
					/>
					{addressError &&
						Object.entries(addressError).map((error: any) => (
							<AlertBanner
								key={`addAddressError_${error.message}`}
								colour={Colour.Alert}
								title='Error'
								content={error.toString()}
							/>
						))}
					{emailValid.toString()} - {telephoneValid.toString()}
					<ButtonSubmit
						title={
							props.existingData ? t('address.actions.updateAddress') : t('address.actions.addAddress')
						}
						loading={addressSaving}
						disabled={
							!isValid || !emailValid || !telephoneValid || telephoneLookupActive || emailLookupActive
						}
						fullWidth
						colour={ColourStateSolid.Primary}
					/>
				</fieldset>
			</form>
		</FormProvider>
	);
}

export { AddAddress };
